import {computed, type ComputedRef, type Ref} from 'vue';
import type {ReccoTopicDTO} from '@/ts/types/dto/recco/topic.dto';
import {LibraryContentTypeDTO, type ExploreLibraryItemDTO} from '@/ts/types/dto/recco/library.dto';
import {orderStartedFirst} from '@/ts/utils/explore-catalog-order';

/** A topic rail: the topic and the educational items grouped under it. */
export interface ExploreTopicRail {
  topic: ReccoTopicDTO;
  items: ExploreLibraryItemDTO[];
}

/** Sections derived from the flat library, returned by {@link useExploreLibraryGrouping}. */
export interface ExploreLibraryGrouping {
  /**
   * Org-featured items (IPD-2942) — their own section, taking precedence over "For you".
   * `recommended` is stripped so the cards carry the partner badge, never the pill.
   */
  featuredItems: ComputedRef<ExploreLibraryItemDTO[]>;
  /** Recommended items head the "For you" rail. */
  recommendedItems: ComputedRef<ExploreLibraryItemDTO[]>;
  challengeItems: ComputedRef<ExploreLibraryItemDTO[]>;
  /**
   * One rail per available topic that has educational items; empty rails are dropped. An item
   * tagged with several topics appears in each of their rails (IPD-2936).
   */
  topicRails: ComputedRef<ExploreTopicRail[]>;
  /**
   * Educational items matching none of `availableTopics` (the topic list comes from a
   * different endpoint than the library, so they can diverge). Without this they would match
   * no topic rail and silently disappear — they render in a fallback rail.
   */
  otherEducationalItems: ComputedRef<ExploreLibraryItemDTO[]>;
  /** True when any section has at least one item — distinguishes results from the empty state. */
  hasResults: ComputedRef<boolean>;
}

/**
 * Re-groups the flat Explore library into the section structure (IPD-2935 §2: filter within
 * sections, hide empty, no flat grid). Pure — no side effects, no i18n (the page resolves rail
 * titles from the shared label-key maps).
 *
 * "For you" and the featured band are additional surfaces, not owners: a recommended or
 * org-featured item still appears in its topic rail (§4 — the Recommended pill shows "in the
 * rail and wherever else they appear on the page"), and an item tagged with several topics
 * appears in each (IPD-2936).
 *
 * Membership has to match how the unfiltered catalog is built, or a rail present by default
 * vanishes as soon as a filter is applied — the defect this composable was fixed for
 * (IPD-3032). That is why featured items stay in their rail too: an org whose only Relaxation
 * content is featured would otherwise lose that rail the moment the user filtered by it.
 */
export function useExploreLibraryGrouping(
  items: Ref<ExploreLibraryItemDTO[]>,
  availableTopics: Ref<ReccoTopicDTO[]>,
): ExploreLibraryGrouping {
  // Only educational items carry `content`, so this can't match a challenge or a program.
  function isFeatured(item: ExploreLibraryItemDTO): boolean {
    return Boolean(item.content?.isFeatured);
  }

  const featuredItems = computed<ExploreLibraryItemDTO[]>(() =>
    items.value.filter(isFeatured).map((item) => ({...item, recommended: false})));

  // Programs never surface in Explore, recommended or not (see educationalItems below).
  const recommendedItems = computed<ExploreLibraryItemDTO[]>(() =>
    items.value.filter((item) =>
      !isFeatured(item) && item.recommended && item.contentType !== LibraryContentTypeDTO.Program));

  // Started items lead their section (highest progress first); the rest keep relevance order.
  const challengeItems = computed<ExploreLibraryItemDTO[]>(() =>
    orderStartedFirst(items.value.filter((item) => item.contentType === LibraryContentTypeDTO.Challenge)));

  // Educational content — and, defensively, anything that isn't a challenge or a
  // program — so an item with an unexpected contentType still lands in a topic rail
  // or the fallback rather than silently disappearing. Programs are deliberately
  // excluded AND not surfaced anywhere: today's program data is medical licensing
  // plumbing (the user-facing Programs launch later with a model TBD).
  const educationalItems = computed<ExploreLibraryItemDTO[]>(() =>
    items.value.filter((item) =>
      item.contentType !== LibraryContentTypeDTO.Challenge
      && item.contentType !== LibraryContentTypeDTO.Program));

  const topicRails = computed<ExploreTopicRail[]>(() =>
    availableTopics.value
      .map((topic) => ({
        topic,
        items: educationalItems.value.filter((item) => (item.topics ?? []).includes(topic)),
      }))
      .filter((rail) => rail.items.length > 0));

  const otherEducationalItems = computed<ExploreLibraryItemDTO[]>(() => {
    const knownTopics = new Set(availableTopics.value);
    return educationalItems.value.filter((item) => !(item.topics ?? []).some((topic) => knownTopics.has(topic)));
  });

  const hasResults = computed<boolean>(() =>
    featuredItems.value.length > 0
    || recommendedItems.value.length > 0
    || challengeItems.value.length > 0
    || topicRails.value.length > 0
    || otherEducationalItems.value.length > 0);

  return {featuredItems, recommendedItems, challengeItems, topicRails, otherEducationalItems, hasResults};
}
